import axios from "axios";
import keycloakInstance from "@/initKeycloak";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";
import type { ConciergeMode, PlanResponse } from "./types";

/** Feature flag — ship the welcome flow with this off until the backend is
 *  live on the environment. */
export const conciergeEnabled =
  import.meta.env.VITE_CONCIERGE_ENABLED === "true";

// Same-origin through Caddy (/api/concierge/* -> concierge droplet).
const baseURL = import.meta.env.VITE_CONCIERGE_BASE_URL || "/api/concierge";

const client = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  // Onboarding plans routinely take 30-60s (thinking + a big multi-asset
  // profile), and the server retries once on validation failure — 60s was
  // observed timing out on a real 6-asset-class query.
  timeout: 150_000,
});

client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30);
    } catch (error) {
      console.error("Token refresh error in concierge interceptor:", error);
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

attachRetryOn401(client);

export async function requestPlan(
  query: string,
  mode: ConciergeMode = "onboarding",
  context?: {
    existing_portfolio_names?: string[];
    existing_tag_watchlist_names?: string[];
    existing_query_names?: string[];
  }
): Promise<PlanResponse> {
  const { data } = await client.post<PlanResponse>("/plan", {
    query,
    mode,
    context: context ?? {},
  });
  if (data.schema_version !== 1) {
    throw new Error("Unsupported plan version — please refresh the app.");
  }
  return data;
}
